/**
 * Réservation publique d'un cours particulier (style Calendly) — accessible
 * sans compte depuis la fiche publique marketing d'un tuteur. Le compte élève
 * est créé au paiement (webhook Stripe, calque /acheter + /bienvenue) si
 * l'email saisi n'a pas déjà de compte ; sinon la réservation est rattachée
 * au compte existant. Si le visiteur est déjà connecté dans l'app, son JWT est
 * automatiquement utilisé à la place des champs invité (cf. tutor-booking-checkout).
 */

import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button, CompassLogo, Input, Label } from '@jeraisonne/ui';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { useBookableSlot, useBookSlot } from '@/lib/booking/booking-slots';

function formatSlotDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function levelLabel(level: string): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;
}

export function ReserverPage() {
  const { slotId } = useParams({ from: '/reserver/$slotId' });
  const { data: slot, isLoading } = useBookableSlot(slotId);
  const book = useBookSlot();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setFormError(null);
    if (!slotId) return;
    if (!email.trim() || !email.includes('@')) {
      setFormError('Renseigne un email valide pour confirmer ta réservation.');
      return;
    }
    try {
      const { url } = await book.mutateAsync({
        slotId,
        returnTo: window.location.href,
        guestEmail: email.trim(),
        guestName: name.trim() || undefined,
      });
      window.location.href = url;
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Réservation impossible pour le moment.');
    }
  };

  return (
    <div className="bg-ink-50 native:pt-[env(safe-area-inset-top)] min-h-dvh px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-md space-y-5">
        <div className="flex justify-center pt-2">
          <CompassLogo size="md" />
        </div>

        {isLoading && (
          <div className="flex justify-center py-10">
            <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          </div>
        )}

        {!isLoading && !slot && (
          <div className="space-y-1 rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-black/5">
            <p className="text-2xl">🔍</p>
            <p className="text-ink-700 font-sans font-semibold">Créneau introuvable</p>
            <p className="text-ink-500 font-sans text-sm">
              Ce lien de réservation n'est plus valide.
            </p>
          </div>
        )}

        {slot && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
              <p className="font-display text-ink-400 text-[11px] font-bold uppercase tracking-widest">
                Cours particulier avec {slot.tutor_name}
              </p>
              <p className="font-display text-ink-900 mt-1 text-lg font-extrabold capitalize">
                {formatSlotDate(slot.starts_at)}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="bg-primary-50 text-primary-700 rounded-full px-2.5 py-1 text-xs font-semibold">
                  {levelLabel(slot.level)}
                </span>
                {slot.chapter_title && (
                  <span className="bg-ink-100 text-ink-700 rounded-full px-2.5 py-1 text-xs font-semibold">
                    {slot.chapter_title}
                  </span>
                )}
              </div>
              <div className="border-ink-100 mt-4 flex items-center justify-between border-t pt-4">
                <p className="text-ink-500 font-sans text-xs">
                  {slot.seats_left > 0
                    ? `${slot.seats_left} place${slot.seats_left > 1 ? 's' : ''} restante${slot.seats_left > 1 ? 's' : ''} sur ${slot.capacity}`
                    : 'Complet'}
                </p>
                <p className="font-display text-ink-900 text-xl font-extrabold">
                  {formatPrice(slot.price_cents)}
                </p>
              </div>
            </div>

            {!slot.bookable && (
              <div className="rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-black/5">
                <p className="text-ink-700 font-sans text-sm font-semibold">
                  Ce créneau n'est plus réservable
                </p>
                <p className="text-ink-500 mt-1 font-sans text-xs">
                  Le paiement doit être finalisé au moins 1h avant le début du cours.
                </p>
              </div>
            )}

            {slot.bookable && slot.seats_left <= 0 && (
              <div className="rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-black/5">
                <p className="text-ink-700 font-sans text-sm font-semibold">
                  Ce créneau est complet
                </p>
              </div>
            )}

            {slot.bookable && slot.seats_left > 0 && (
              <div className="flex flex-col gap-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5">
                <div>
                  <Label htmlFor="guest-email" required>
                    Email
                  </Label>
                  <Input
                    id="guest-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="toi@exemple.fr"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <Label htmlFor="guest-name">Prénom (optionnel)</Label>
                  <Input
                    id="guest-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ton prénom"
                    autoComplete="given-name"
                  />
                </div>
                {formError && <p className="text-accent-600 font-sans text-xs">{formError}</p>}
                <Button onClick={handleSubmit} disabled={book.isPending} className="mt-1 w-full">
                  {book.isPending
                    ? 'Redirection…'
                    : `Payer ${formatPrice(slot.price_cents)} et réserver`}
                </Button>
                <p className="text-ink-400 font-sans text-[11px]">
                  Le paiement confirme ta place. Passé un délai d'1h avant le cours, la réservation
                  expire automatiquement si elle n'est pas payée.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
